import { Add, MergeType } from '@mui/icons-material'
import { TabPanel } from '@mui/lab'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import { Box, Button, Stack, Tab } from '@mui/material'
import { useState } from 'react'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { Link } from 'react-router-dom'
import {
    CandidateSqlSearchProvider,
    RosterSqlSearchProvider,
} from 'recruiter/contexts'
import { recruiterRouteConfig } from 'recruiter/routes'
import { DEFAULT_STATUSES } from 'shared/hooks/rosterFilterState'
import { toGraphQLRosterFilters } from 'shared/hooks/rosterSqlSearchUrlState'
import { getPxAsRem } from 'shared/utils'
import { candidateTableColumns } from 'recruiter/components/candidateTableColumns'
import AddCandidateForm from '../Candidates/AddCandidate'
import { CandidateSearchSql } from './CandidateSearchSql'
import { ResearchBulkActions } from './ResearchBulkActions'
import { RosterSearch } from './RosterSearch'
import SaveMapping from './SaveMapping'

type SearchViewTab = 'candidates' | 'rosters'

interface SearchViewProps {
    /** The tab that this view should default to when rendered. */
    initialTab?: SearchViewTab
}

export function SearchView({ initialTab = 'candidates' }: SearchViewProps) {
    usePageTitle(
        `Research · ${initialTab === 'rosters' ? 'Searches' : 'Candidates'}`
    )
    const [addModalOpen, setAddModalOpen] = useState(false)

    const addCandidate = (
        <Button
            variant="contained"
            size="small"
            startIcon={<Add sx={{ fontSize: '1rem !important' }} />}
            onClick={() => setAddModalOpen(true)}
            sx={{
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                fontWeight: 500,
                py: 0.5,
                px: 1.5,
            }}
        >
            New Candidate
        </Button>
    )

    return (
        <TabContext value={initialTab}>
            <AddCandidateForm
                open={addModalOpen}
                onClose={() => setAddModalOpen(false)}
            />
            <Stack direction="column" height="100%">
                <Stack
                    justifyContent="space-between"
                    direction="row"
                    alignItems="flex-start"
                    sx={{
                        backgroundColor: 'white',
                        borderBottom: 1,
                        borderColor: 'divider',
                        paddingTop: getPxAsRem(16),
                        paddingX: getPxAsRem(16),
                    }}
                >
                    <Box>
                        <TabList aria-label="Research tabs">
                            <Tab
                                label="Candidates"
                                value="candidates"
                                component={Link}
                                to={`${recruiterRouteConfig.research.path}/candidates`}
                            />
                            <Tab
                                label="Searches"
                                value="rosters"
                                component={Link}
                                to={`${recruiterRouteConfig.research.path}/searches`}
                            />
                        </TabList>
                    </Box>
                </Stack>

                <TabPanel
                    value="candidates"
                    sx={{
                        flex: 1,
                        overflow: 'hidden',
                        padding: getPxAsRem(24),
                    }}
                >
                    <CandidateSqlSearchProvider>
                        <CandidateSearchSql
                            columns={candidateTableColumns}
                            pinnedColumns={{ left: ['candidate'] }}
                            topActionButtons={
                                <>
                                    <ResearchBulkActions />
                                    <Box sx={{ display: 'none' }}>
                                        <SaveMapping />
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<MergeType />}
                                        component={Link}
                                        to={
                                            recruiterRouteConfig.mergeCandidates
                                                .path
                                        }
                                        sx={{
                                            textTransform: 'uppercase',
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            py: 0.5,
                                            px: 1.5,
                                        }}
                                    >
                                        Merge Candidates
                                    </Button>
                                    {addCandidate}
                                </>
                            }
                        />
                    </CandidateSqlSearchProvider>
                </TabPanel>

                <TabPanel
                    value="rosters"
                    sx={{
                        flex: 1,
                        overflow: 'hidden',
                        padding: getPxAsRem(24),
                    }}
                >
                    <RosterSqlSearchProvider
                        defaultStatuses={DEFAULT_STATUSES}
                        transformFilters={toGraphQLRosterFilters}
                    >
                        <RosterSearch />
                    </RosterSqlSearchProvider>
                </TabPanel>
            </Stack>
        </TabContext>
    )
}
